import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { creditValueCentsToCredits, formatCreditsValue, formatUsd, creditsToUsd } from '@/utils/credits';
import { jsonFetch } from '@/utils/jsonFetch';
import type { ParentHomePayload, StudentSummary } from './ParentHome';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

type WalletResponse = {
  wallet: {
    balanceCredits: number;
  };
};

type TeacherOption = {
  key: string;
  studentId: string;
  studentName: string;
  classroomName: string;
  teacherName: string;
  teacherUserId: string;
};

type CreditPack = {
  id: string;
  name: string;
  usd_price: number;
  credit_value_cents: number;
};

export default function ParentWallet() {
  const [wallet, setWallet] = useState<WalletResponse['wallet'] | null>(null);
  const [students, setStudents] = useState<StudentSummary[]>([]);
  const [walletError, setWalletError] = useState<string | null>(null);
  const [studentsError, setStudentsError] = useState<string | null>(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [packsLoading, setPacksLoading] = useState(true);
  const [packsError, setPacksError] = useState<string | null>(null);
  const [buyingPack, setBuyingPack] = useState<string | null>(null);

  const loadWallet = useCallback(async () => {
    setWalletLoading(true);
    setWalletError(null);
    try {
      const data = await jsonFetch<WalletResponse>('/api/guardian/wallet');
      setWallet(data?.wallet ?? null);
    } catch (err) {
      console.warn('[ParentWallet] failed to load wallet', err);
      setWallet(null);
      setWalletError(err instanceof Error ? err.message : 'Unable to load wallet');
    } finally {
      setWalletLoading(false);
    }
  }, []);

  const loadStudents = useCallback(async () => {
    setStudentsLoading(true);
    setStudentsError(null);
    try {
      const data = await jsonFetch<ParentHomePayload>('/api/parent/home');
      setStudents(Array.isArray(data?.students) ? data.students : []);
    } catch (err) {
      console.warn('[ParentWallet] failed to load students', err);
      setStudents([]);
      setStudentsError(err instanceof Error ? err.message : 'Unable to load linked students');
    } finally {
      setStudentsLoading(false);
    }
  }, []);

  const loadPacks = useCallback(async () => {
    setPacksLoading(true);
    setPacksError(null);
    try {
      const raw = await jsonFetch<{ data?: { items: CreditPack[] }; items?: CreditPack[] }>(`${CORE_API}/api/v1/sponsors/packs`);
      const payload = (raw as any)?.data ?? raw;
      setPacks(Array.isArray(payload?.items) ? payload.items : []);
    } catch (err) {
      console.warn('[ParentWallet] failed to load packs', err);
      setPacks([]);
      setPacksError(err instanceof Error ? err.message : 'Unable to load credit packs');
    } finally {
      setPacksLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWallet();
    loadStudents();
    loadPacks();
  }, [loadStudents, loadWallet, loadPacks]);

  const startCheckout = async (pack: CreditPack) => {
    setBuyingPack(pack.id);
    try {
      const raw = await jsonFetch<{ data?: { url: string }; url?: string }>(`${CORE_API}/api/v1/checkout/guardian`, {
        method: 'POST',
        body: JSON.stringify({ pack: pack.name }),
      });
      const checkoutUrl = (raw as any)?.data?.url ?? (raw as any)?.url;
      if (checkoutUrl && typeof window !== 'undefined') {
        window.location.assign(checkoutUrl);
      }
    } catch (err) {
      console.warn('[ParentWallet] checkout failed', err);
      setPacksError(err instanceof Error ? err.message : 'Unable to start checkout');
    } finally {
      setBuyingPack(null);
    }
  };

  const balanceLabel = useMemo(() => formatCreditsValue(wallet?.balanceCredits ?? 0), [wallet?.balanceCredits]);

  const teacherOptions = useMemo(() => {
    const options: TeacherOption[] = [];
    const seen = new Set<string>();
    students.forEach((student) => {
      const teacherUserId = student.classroom?.teacherUserId;
      if (!teacherUserId) return;
      const key = `${student.id}-${teacherUserId}`;
      if (seen.has(key)) return;
      seen.add(key);
      const studentName = student.displayName || student.fullName || 'Student';
      const classroomName = student.classroom?.name || 'Classroom';
      const teacherName = student.classroom?.teacherName || 'Teacher';
      options.push({
        key,
        studentId: student.id,
        studentName,
        classroomName,
        teacherName,
        teacherUserId,
      });
    });
    return options;
  }, [students]);

  const selectedOption = teacherOptions.find((option) => option.key === selectedKey) ?? null;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const parsedAmount = Number.parseInt(amount, 10);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setStatus('error');
      setMessage('Enter a positive credit amount.');
      return;
    }

    if (!selectedOption) {
      setStatus('error');
      setMessage('Select a classroom teacher.');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      await jsonFetch('/api/wallet/transfer', {
        method: 'POST',
        body: JSON.stringify({
          toUserId: selectedOption.teacherUserId,
          amount: parsedAmount,
          note: note.trim() ? note.trim() : `Parent transfer for ${selectedOption.studentName}`,
          typeOut: 'family_transfer',
          typeIn: 'family_transfer_in',
          source: 'guardian_wallet',
        }),
      });

      setStatus('success');
      setMessage('Credits sent to the classroom teacher!');
      setAmount('');
      setNote('');

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('tower-wallet-refresh'));
      }

      await loadWallet();
    } catch (err) {
      console.warn('[ParentWallet] transfer failed', err);
      setStatus('error');
      setMessage(err instanceof Error ? err.message : 'Unable to send credits right now.');
    }
  };

  return (
    <div className="PortalShell">
      <header className="PortalHeader">
        <h1>Parent Credits Wallet</h1>
        <p>Support your student&apos;s classroom with Tower credits.</p>
      </header>

      <section className="Card space-y-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-cocoa/60">Current balance</p>
          <p className="text-3xl font-semibold text-cocoa">{walletLoading ? '—' : `${balanceLabel} credits`}</p>
        </div>
        {walletError ? <p className="text-sm text-brick">{walletError}</p> : null}
        <p className="text-sm text-cocoa/70">Credits stay on Tower and power teacher tools.</p>
      </section>

      <section className="Card space-y-4">
        <h2 className="Title">Send credits to a teacher</h2>
        {studentsLoading ? (
          <p className="text-sm text-cocoa/60">Loading linked students…</p>
        ) : teacherOptions.length === 0 ? (
          <p className="text-sm text-cocoa/70">
            No classroom teachers available yet. Ask your teacher to link your guardian account.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block text-sm font-medium text-cocoa">
              Classroom teacher
              <select
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2"
                value={selectedKey}
                onChange={(event) => setSelectedKey(event.target.value)}
              >
                <option value="">Select a teacher</option>
                {teacherOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.studentName} • {option.classroomName} ({option.teacherName})
                  </option>
                ))}
              </select>
            </label>

            <label className="block text-sm font-medium text-cocoa">
              Credits to send
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2"
                value={amount}
                onChange={(event) => setAmount(event.target.value.replace(/[^0-9]/g, ''))}
                placeholder="300"
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </label>

            <label className="block text-sm font-medium text-cocoa">
              Optional note
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Add a short message"
              />
            </label>

            <button type="submit" className="Button primary w-full" disabled={status === 'loading'}>
              {status === 'loading' ? 'Sending…' : 'Send credits'}
            </button>
            {message ? (
              <p className={`text-sm ${status === 'error' ? 'text-brick' : 'text-emerald-600'}`}>{message}</p>
            ) : null}
          </form>
        )}
        {studentsError ? <p className="text-sm text-brick">{studentsError}</p> : null}
      </section>

      <section className="Card space-y-4">
        <h2 className="Title">Buy Tower Credits</h2>
        <p className="text-sm text-cocoa/70">Purchase credit packs to support your student's classroom.</p>
        {packsError ? <p className="text-sm text-brick">{packsError}</p> : null}
        {packsLoading ? (
          <p className="text-sm text-cocoa/60">Loading packs…</p>
        ) : packs.length === 0 ? (
          <p className="text-sm text-cocoa/60">No credit packs available right now.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {packs.map((pack) => {
              const credits = creditValueCentsToCredits(pack.credit_value_cents);
              return (
                <div key={pack.id} className="rounded-xl border border-sand bg-white p-4 space-y-2">
                  <p className="text-xs uppercase tracking-wide text-cocoa/60">{pack.name}</p>
                  <p className="text-2xl font-semibold text-cocoa">
                    ${(Number(pack.usd_price) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-cocoa/60">
                    {formatCreditsValue(credits)} credits (≈ {formatUsd(creditsToUsd(credits))})
                  </p>
                  <button
                    type="button"
                    onClick={() => startCheckout(pack)}
                    disabled={buyingPack === pack.id}
                    className="Button primary w-full"
                  >
                    {buyingPack === pack.id ? 'Redirecting…' : 'Buy pack'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
